import { useEffect, useRef, useState } from 'react';

import { CloseIcon, LightningIcon } from '@braskai/icons';

import { Box, Modal, ModalProps, useMediaQuery, useTheme } from '@mui/material';

import { BOOK_SALES_DEMO_SOURCES, useOpenBookSalesDemo } from '@/entities/demoBooking';
import { usePricingExperimentExposure } from '@/entities/pricing';

import { PricingAnalyticsProvider, usePricingAnalyticsProperties } from '@/shared/contexts/PricingAnalyticsContext';
import { useInterfaceLanguage } from '@/shared/i18n';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { PaymentInterval, Plan } from '@/shared/types/Billing';
import { Loader } from '@/shared/ui/Loader';

import { useBilling } from '../../model/useBilling';
import { useBuyPlanButton } from '../../model/useBuyPlanButton';
import { useTrialPaywallExperimentModalStore } from '../../model/useTrialPaywallExperimentModalStore';
import { PaywallBillingToggle } from './components/PaywallBillingToggle';
import { PaywallEnterpriseCard } from './components/PaywallEnterpriseCard';
import { PaywallPlanCard } from './components/PaywallPlanCard';
import { SCloseButton, SDialog, SFooterButton, SOverlay, SPricingChip } from './styled';

type CloseMethod = 'backdrop' | 'close_button' | 'continue_trial' | 'escape';

const LOADER_HEIGHT = 580;

const getDefaultPlan = (plans: Plan[]) => plans.find((plan) => plan.metadata.bundle_value_default) ?? plans[0];

const TrialPaywallExperimentModalContent = () => {
  const { t } = useInterfaceLanguage();
  const theme = useTheme();
  const isCompact = !useMediaQuery(theme.breakpoints.up('laptop'), { noSsr: true });
  const close = useTrialPaywallExperimentModalStore((state) => state.close);
  const openBookSalesDemo = useOpenBookSalesDemo();
  const analyticsProperties = usePricingAnalyticsProperties();

  const [period, setPeriod] = useState(PaymentInterval.Year);
  const [chosenPriceId, setChosenPriceId] = useState<null | string>(null);
  const isBackdropPressRef = useRef(false);
  const { error, plansGroupByCategory, products } = useBilling({ showErrors: false });
  const { handleBuyPlanButtonClick } = useBuyPlanButton({
    paymentPeriod: period,
    source: 'trialPaywallExperiment',
    setChosenPlanId: setChosenPriceId,
  });

  const plans = plansGroupByCategory.map(({ plans: categoryPlans }) => getDefaultPlan(categoryPlans));
  const isReady = plans.length > 0;
  const isUnavailable = Boolean(error) || (Boolean(products) && !isReady);

  usePricingExperimentExposure(
    products,
    isReady,
    MixpanelEvents.trialPaywallExperimentShown,
    true,
    'trial_paywall_experiment',
  );

  useEffect(() => {
    if (isUnavailable) close();
  }, [close, isUnavailable]);
  const isPurchaseInProgress = Boolean(chosenPriceId);
  const billing = period === PaymentInterval.Year ? 'annual' : 'monthly';

  const handleClose = (method: CloseMethod) => {
    MixpanelEvents.trialPaywallExperimentClosed({ method });
    close();
  };

  const handleModalClose: ModalProps['onClose'] = (_event, reason) => {
    handleClose(reason === 'escapeKeyDown' ? 'escape' : 'backdrop');
  };

  const handlePeriodChange = (nextPeriod: PaymentInterval) => {
    (nextPeriod === PaymentInterval.Year
      ? MixpanelEvents.trialPaywallExperimentSwitchAnnual
      : MixpanelEvents.trialPaywallExperimentSwitchMonthly)();
    setPeriod(nextPeriod);
  };

  const handleGetStarted = (plan: Plan, priceId: string) => {
    MixpanelEvents.trialPaywallExperimentPlanGetStartedClick({ billing, plan: plan.category });
    MixpanelEvents.choosePlan({ ...analyticsProperties, name: plan.name, subscription: billing });
    void handleBuyPlanButtonClick(priceId, plan);
  };

  const handleContactSales = (plan?: Plan) => {
    MixpanelEvents.trialPaywallExperimentContactSalesClick({ plan: plan?.category ?? 'enterprise' });
    openBookSalesDemo(
      plan
        ? BOOK_SALES_DEMO_SOURCES.trialPaywallExperimentBusiness
        : BOOK_SALES_DEMO_SOURCES.trialPaywallExperimentEnterprise,
      t('pricing.actions.contactUs'),
      analyticsProperties.pricing_entry_point,
    );
  };

  const toggle = <PaywallBillingToggle isCompact={isCompact} period={period} onChange={handlePeriodChange} />;
  const title = t('pricing.header.title');
  const description = t('pricing.trialPaywallExperiment.description');
  const closeButton = (
    <SCloseButton
      $isCompact={isCompact}
      aria-label={t('pricing.modal.closeAria')}
      data-testid="trial-paywall-experiment-close"
      onClick={() => handleClose('close_button')}
    >
      <CloseIcon height={20} width={20} />
    </SCloseButton>
  );

  const cards = (
    <>
      {plans.map((plan) => (
        <PaywallPlanCard
          key={plan.id}
          isCompact={isCompact}
          isLoading={plan.prices.some((price) => price.id === chosenPriceId)}
          isPurchaseInProgress={isPurchaseInProgress}
          period={period}
          plan={plan}
          onContactSales={handleContactSales}
          onGetStarted={handleGetStarted}
        />
      ))}
      <PaywallEnterpriseCard
        isCompact={isCompact}
        isPurchaseInProgress={isPurchaseInProgress}
        onContactSales={() => handleContactSales()}
      />
    </>
  );

  return (
    <Modal hideBackdrop open onClose={handleModalClose}>
      <SOverlay
        $isCompact={isCompact}
        data-testid="trial-paywall-experiment-overlay"
        onClick={(event) => {
          if (isBackdropPressRef.current && event.target === event.currentTarget) handleClose('backdrop');
          isBackdropPressRef.current = false;
        }}
        onMouseDown={(event) => {
          isBackdropPressRef.current = event.target === event.currentTarget;
        }}
      >
        <SDialog
          $isCompact={isCompact}
          aria-busy={!isReady}
          aria-label={title}
          aria-modal="true"
          data-testid="trial-paywall-experiment"
          role="dialog"
        >
          {isCompact ? (
            <Box
              sx={{
                borderBottom: `1px solid ${theme.palette.grey[100]}`,
                display: 'flex',
                flex: 'none',
                flexDirection: 'column',
                gap: 14,
                padding: '20px 16px 14px',
              }}
            >
              <Box sx={{ alignItems: 'flex-start', display: 'flex', gap: 12 }}>
                <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', gap: 4, minWidth: 0 }}>
                  <Box
                    sx={{
                      fontSize: 22,
                      fontWeight: 600,
                      letterSpacing: '-0.01em',
                      lineHeight: '28px',
                      margin: 0,
                      textWrap: 'balance',
                    }}
                    component="h2"
                  >
                    {title}
                  </Box>
                  <Box
                    component="p"
                    sx={{ color: 'grey.600', fontSize: 13, lineHeight: '18px', margin: 0, textWrap: 'pretty' }}
                  >
                    {description}
                  </Box>
                </Box>
                {closeButton}
              </Box>
              {toggle}
            </Box>
          ) : (
            <>
              {closeButton}
              <Box
                sx={{
                  alignItems: 'flex-end',
                  display: 'flex',
                  flex: 'none',
                  gap: 24,
                  justifyContent: 'space-between',
                  padding: '32px 72px 8px 32px',
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
                  <SPricingChip>
                    <LightningIcon height={12} width={12} />
                    {t('pricing.title')}
                  </SPricingChip>
                  <Box component="h2" sx={{ letterSpacing: '-0.01em', margin: 0, typography: 'title-2-sb' }}>
                    {title}
                  </Box>
                  <Box component="p" sx={{ color: 'grey.600', margin: 0, typography: 'body-2' }}>
                    {description}
                  </Box>
                </Box>
                {toggle}
              </Box>
            </>
          )}

          {isReady ? (
            <Box
              sx={{
                display: 'flex',
                flex: '1 1 auto',
                flexDirection: 'column',
                gap: isCompact ? 24 : 20,
                minHeight: 0,
                overflowY: 'auto',
                padding: isCompact ? '28px 16px 20px' : '32px 32px 24px',
              }}
            >
              {isCompact ? (
                cards
              ) : (
                <Box
                  sx={{
                    columnGap: 12,
                    display: 'grid',
                    gridTemplateColumns: `repeat(${plans.length + 1}, minmax(0, 1fr))`,
                    gridTemplateRows: 'auto auto auto auto 1fr auto',
                  }}
                >
                  {cards}
                </Box>
              )}
            </Box>
          ) : (
            <Box
              data-testid="trial-paywall-experiment-loader"
              sx={{ flex: isCompact ? '1 1 auto' : `0 1 ${LOADER_HEIGHT}px`, minHeight: 0, position: 'relative' }}
            >
              <Loader />
            </Box>
          )}

          <Box
            sx={
              isCompact
                ? {
                    background: theme.palette.common.white,
                    borderTop: `1px solid ${theme.palette.grey[200]}`,
                    flex: 'none',
                    padding: '8px 16px 20px',
                  }
                : { display: 'flex', flex: 'none', justifyContent: 'center', padding: '0 32px 24px' }
            }
          >
            <SFooterButton
              $isCompact={isCompact}
              data-testid="trial-paywall-experiment-continue"
              onClick={() => handleClose('continue_trial')}
            >
              {t('pricing.trial.continue')}
            </SFooterButton>
          </Box>
        </SDialog>
      </SOverlay>
    </Modal>
  );
};

export const TrialPaywallExperimentModal = () => (
  <PricingAnalyticsProvider entryPoint="trial_paywall_experiment">
    <TrialPaywallExperimentModalContent />
  </PricingAnalyticsProvider>
);
